import { useDispatch, useSelector } from 'react-redux';
import { getUserCategories, addUserCategory, removeUserCategory } from 'store/categories/actions';
import { getVersion } from 'store/version/actions';
import { resetItemsCategories } from 'store/items-categories/actions';
import {
  useEffect, useState, useRef, useCallback, Fragment,
} from 'react';
import {
  getUserCategories as getUserCategoriesSelector,
  getUserCategoriesLoadingState as getUserCategoriesLoadingStateSelector,
} from 'store/categories/selectors';
import { getIsUserSubscribed, getUser as getUserSelector } from 'store/users/selectors';
import { getVersion as getVersionSelector } from 'store/version/selectors';
import {
  Link,
  Redirect, Route, Switch, useLocation,
} from 'react-router-dom';
import { TOKEN } from 'library/constants/auth';
import { ReactComponent as EvernoteLogo } from 'assets/images/evernote-icon.svg';
import Info from './info';
import MembershipType from './membership-type';
import Categories from './categories';
import About from './about';
import {
  Container, TabMenuContainer, ButtonFile, ButtonFileContainer, CloseIcon,
} from './styles';
import withNavigationFilters from '../../../library/hocs/with-navigation-filters';
import { logout } from '../../../library/auth';
import ROUTES from '../../../library/constants/routes';
import { TermsAndConditions } from './terms-and-conditions';
import { PrivacyPolicy } from './privacy-policy';
import { Icon } from '../../../library/components/add-button/styles';

const TAB_KEYS = {
  GENERAL: 'general',
  CATEGORIES: 'categories',
  PRIVACY_POLICY: 'privacy-policy',
  TERMS: 'terms-and-conditions',
  TOOLS: 'tools',
};

const TAB_KEYS_LABELS = {
  [TAB_KEYS.GENERAL]: 'General',
  [TAB_KEYS.TOOLS]: 'Tools',
  [TAB_KEYS.CATEGORIES]: 'Categories',
  [TAB_KEYS.PRIVACY_POLICY]: 'Privacy policy',
  [TAB_KEYS.TERMS]: 'Terms and conditions',
};

function Settings(props) {
  const dispatch = useDispatch();
  const userCategories = useSelector(getUserCategoriesSelector);
  const userCategoriesLoading = useSelector(getUserCategoriesLoadingStateSelector);
  const user = useSelector(getUserSelector);
  const version = useSelector(getVersionSelector);
  const [localCategories, setLocalCategories] = useState([]);
  const { pathname } = useLocation();
  const evernoteIframeRef = useRef();
  const [openedTool, setOpenedTool] = useState(null);
  const isUserSubscribed = useSelector(getIsUserSubscribed);

  useEffect(() => {
    dispatch(getUserCategories());
    dispatch(getVersion());

    return () => {
      dispatch(resetItemsCategories());
    };
  }, []);

  useEffect(() => {
    if(openedTool && !pathname.endsWith(TAB_KEYS.TOOLS)) {
      setOpenedTool(null);
    }
  }, [openedTool && !pathname.endsWith(TAB_KEYS.TOOLS)]);

  const onIframeLoad = useCallback(() => {
    window.setTimeout(() => {
      evernoteIframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          type: 'WIDGET_AUTH',
          token: localStorage.getItem(TOKEN),
        }),
        process.env.REACT_APP_EVERNOTE_URL,
      );
    }, 1000);
  }, []);

  useEffect(() => {
    if(pathname?.endsWith(TAB_KEYS.TOOLS) && openedTool === 'evernote') {
      evernoteIframeRef.current?.addEventListener('load', onIframeLoad);
    } else {
      evernoteIframeRef.current?.removeEventListener('load', onIframeLoad);
    }

    return () => {
      evernoteIframeRef.current?.removeEventListener('load', onIframeLoad);
    };
  }, [pathname, openedTool]);

  useEffect(() => {
    if (!userCategoriesLoading) {
      setLocalCategories(userCategories.filter(({ active }) => active).map(({ id }) => id));
    }
  }, [userCategoriesLoading]);

  const handleLogout = () => logout(props.history);

  const handleCategoryChange = (id, checked) => {
    const { name } = userCategories.find((category) => id === category.id);
    const newValue = [
      ...(
        checked ? [...localCategories, id] : localCategories.filter((item) => item !== id)
      ),
    ];

    setLocalCategories(newValue);

    if (checked) {
      dispatch(addUserCategory({ id, name }));
    } else {
      dispatch(removeUserCategory({ id, name }));
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      <TabMenuContainer>
        <div style={{
          display: 'flex',
          gap: '12px',
          spacing: '4px',
          padding: '0 32px',
          // width: '860px',
        }}
        >
          {Object.values(TAB_KEYS).map((tab) => (
            <Fragment key={tab}>
              <Link
                style={{
                  fontSize: '14px',
                  lineHeight: '16px',
                  padding: '12px 8px',
                  borderBottomWidth: '2px',
                  borderBottomColor: 'transparent',
                  borderBottomStyle: 'solid',
                  ...(pathname?.endsWith(tab) ? { color: '#6C52A2', borderBottomWidth: '2px', borderBottomColor: '#6C52A2' } : { color: 'rgb(163, 175, 179)', cursor: 'pointer' }),
                }}
                to={`${ROUTES.SETTINGS}/${tab}`}
              >
                {TAB_KEYS_LABELS[tab]}
              </Link>
              {
                openedTool && pathname.endsWith(TAB_KEYS.TOOLS) && tab === TAB_KEYS.TOOLS ? (
                  <CloseIcon
                    className="icon-close"
                    onClick={() => {
                      setOpenedTool(null);
                    }}
                  />
                ) : null
              }
            </Fragment>
          ))}
        </div>
      </TabMenuContainer>
      <Container isFullWidth={openedTool !== null}>
        <Switch>
          <Route path={`${ROUTES.SETTINGS}/${TAB_KEYS.CATEGORIES}`} exact>
            <Categories
              data={userCategories}
              selectedData={localCategories}
              onChange={handleCategoryChange}
            />
          </Route>
          <Route path={`${ROUTES.SETTINGS}/${TAB_KEYS.TOOLS}`} exact>
            {
              openedTool === null ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    margin: '16px',
                  }}
                >
                  <ButtonFileContainer>
                    <ButtonFile
                      style={{
                        backgroundColor: isUserSubscribed ? 'inherit' : '#CFD6DE',
                        cursor: isUserSubscribed ? 'pointer' : 'not-allowed',
                      }}
                      title={isUserSubscribed ? 'Evernote import tool' : 'Subscribe to enable Evernote import'}
                      onClick={isUserSubscribed ? () => {
                        setOpenedTool('evernote');
                      } : undefined}
                    >
                      {(!isUserSubscribed) ? (
                        <Icon className="icon-lock" style={{ color: '#6C52A2', fontSize: '32px', fontWeight: 900 }} />
                      ) : <EvernoteLogo />}
                      <span>
                        Evernote import
                      </span>
                    </ButtonFile>
                  </ButtonFileContainer>
                </div>
              ) : null
            }
            {
              openedTool === 'evernote' ? (
                <iframe
                  onLoad={onIframeLoad}
                  ref={evernoteIframeRef}
                  title="evernote"
                  src={`${process.env.REACT_APP_EVERNOTE_URL}/Evernote_Import/aws-enex-importer.html`}
                  style={{
                    width: 'calc(100vw - 74px)',
                    height: 'calc(100vh - 144px)',
                    minHeight: '480px',
                    border: 0,
                  }}
                />
              ) : null
            }
          </Route>
          <Route path={`${ROUTES.SETTINGS}/${TAB_KEYS.GENERAL}`} exact>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
            >
              <Info
                onLogout={handleLogout}
                name={user.name}
                surname={user.surname}
                email={user.email}
              />
              {
                user ? (
                  <MembershipType user={user} type={user?.subscription_status_android || user?.subscription_status} />
                ) : null
              }
              <About user={user} version={version} itemCount={user.itemCount} syncVersion={user?.data_version} />
            </div>
          </Route>
          <Route path={`${ROUTES.SETTINGS}/${TAB_KEYS.TERMS}`} exact>
            <TermsAndConditions />
          </Route>
          <Route path={`${ROUTES.SETTINGS}/${TAB_KEYS.PRIVACY_POLICY}`} exact>
            <PrivacyPolicy />
          </Route>
          <Route path="*">
            <Redirect to={`${ROUTES.SETTINGS}/${TAB_KEYS.GENERAL}`} />
          </Route>
        </Switch>
      </Container>
    </div>
  );
}

export default withNavigationFilters(Settings);
